import { z } from 'zod';
/**
 * Organigrama de la entidad (GUIA §3.15): las dependencias y quién pertenece a cada una viven en la suite y se
 * comparten con todas las apps. Lo que cada app hace con ellas (quién responde, aprueba o firma en SGDEA; con qué
 * proyectos se comparte en Planning) es de la app.
 */
/**
 * Código de una dependencia: empieza por letra mayúscula o número y sigue con mayúsculas, números, punto, guion y
 * guion bajo, hasta 40 (`docs/organigrama.md`, decisión 2: el punto y el guion son por los códigos del ERP; SGDEA
 * separa su ruta con «/», así que no chocan). Las minúsculas se suben antes de validar. No cambia nunca: SGDEA arma
 * con él la ruta de sus áreas y su motor de decisión lo intercambia (`classification.area`).
 */
export const UNIT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._-]*$/;
export const UNIT_CODE_MAX = 40;
export const unitCodeSchema = z.string().trim().toUpperCase().min(1, 'Escribe el código').max(UNIT_CODE_MAX, `A lo sumo ${UNIT_CODE_MAX} caracteres`)
    .regex(UNIT_CODE_PATTERN, 'Solo letras, números, punto, guion y guion bajo, empezando por letra o número');
/**
 * Lleva un nombre o lo que se escribe a código: sin tildes, en mayúsculas, conserva punto y guion y pasa cada tramo
 * de otros caracteres a «_» («Secretaría de Salud» → «SECRETARIA_DE_SALUD», igual que SGDEA; «1.2-A» queda así).
 * `keepTrailing` deja el «_» final mientras se escribe.
 */
export function toUnitCode(s, opts = {}) {
    const out = s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z0-9.-]+/g, '_')
        .replace(opts.keepTrailing ? /^[^A-Z0-9]+/ : /^[^A-Z0-9]+|_+$/g, '').slice(0, UNIT_CODE_MAX);
    return opts.keepTrailing ? out : out.replace(/_+$/, '');
}
export const UNIT_STATUSES = ['ACTIVE', 'ARCHIVED'];
/**
 * `POST /api/v1/service/entities/:slug/units/import`: traspaso único del árbol que la app ya tenía. Si la entidad no
 * tiene organigrama, se crea tal cual; si ya tiene (la segunda app), cada una se empareja por código, luego por nombre
 * bajo el mismo padre, y lo que no empareja se crea. Los miembros se suman, nunca se quitan; un jefe ya puesto no se
 * cambia. `dryRun` devuelve la correspondencia sin escribir.
 */
export const unitsImportSchema = z.object({
    units: z.array(z.object({
        /** Id de la dependencia en la app. */
        localId: z.string().min(1).max(100),
        parentLocalId: z.string().min(1).max(100).nullable().optional(),
        /** Sin código (Planning), la suite lo saca del nombre. */
        code: unitCodeSchema.optional(),
        name: z.string().trim().min(1).max(160),
        description: z.string().trim().max(2000).nullable().optional(),
        managerSub: z.string().nullable().optional(),
        status: z.enum(UNIT_STATUSES).default('ACTIVE'),
        memberSubs: z.array(z.string()).max(5000).default([]),
    })).max(2000),
    dryRun: z.boolean().default(false),
});
/**
 * Consulta de impacto (`POST <provisioning_path>/:slug/units/check`, la suite → la app): antes de archivar, borrar,
 * mover una dependencia o sacar personas de ella, la suite pregunta a cada app viva. La app responde lo que la
 * rompería (`blockers`: la suite no aplica el cambio) o lo que conviene saber (`warnings`: la consola los muestra y
 * aplica al confirmar). 404 = la app no usa el organigrama compartido y no opina.
 */
export const UNIT_CHANGE_KINDS = ['archive', 'delete', 'move', 'removeMembers'];
export const unitChangeCheckRequestSchema = z.object({
    change: z.enum(UNIT_CHANGE_KINDS),
    unitId: z.string().uuid(),
    /** `move`: el padre nuevo (`null` = raíz). */
    parentId: z.string().uuid().nullable().optional(),
    /** `removeMembers`: quiénes salen. */
    subs: z.array(z.string()).optional(),
});
const unitChangeIssueSchema = z.object({ code: z.string().min(1).max(80), message: z.string().min(1).max(500) });
export const unitChangeCheckResponseSchema = z.object({
    blockers: z.array(unitChangeIssueSchema).default([]),
    warnings: z.array(unitChangeIssueSchema).default([]),
});
//# sourceMappingURL=organigrama.js.map